import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { IonLabel, IonList, IonListHeader, IonItem, IonItemDivider, IonText } from '@ionic/angular/standalone';

import { DefinitionsListService } from './definitions-list.service';
import { CrosswordBoardConfig } from '../crossword-board/crossword-board.component';
import { CrosswordWordModel } from '../models/crossword-word.model';
import { CrosswordDirectionsEnum } from '../models/crossword-directions.enum';
import { HelperFunctionsService } from '../../shared-module/helpers/helper-functions.service';
import { SimplebarAngularComponent, SimplebarAngularModule } from 'simplebar-angular';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleKeyboardService } from '../../shared-module/puzzle-softkeyboard/puzzle-keyboard.service';
import { Store } from '@ngxs/store';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { TranslateModule } from '@ngx-translate/core';

interface SplitFilter {
  filter: (word: CrosswordWordModel) => boolean;
  name: string;
}

interface WordList {
  id: string;
  name: string;
  words?: CrosswordWordModel[];
  wordLists?: WordList[];
}

@Component({
  selector: 'lib-puzzle-crossword-sidebar',
  templateUrl: './crossword-sidebar.component.html',
  styleUrls: ['./crossword-sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonLabel,
    IonList,
    IonListHeader,
    IonItem,
    IonItemDivider,
    IonText,
    SimplebarAngularModule,
    TranslateModule,
    DragDropModule,
  ],
})
export class CrosswordSidebarComponent implements OnInit, AfterContentChecked {
  // @ViewChild('inject', { static: true, read: ViewContainerRef }) injectViewContainerRef: ViewContainerRef;
  // @ViewChild('injectHorizontal', { static: true, read: ViewContainerRef }) injectHorizontallyViewContainerRef: ViewContainerRef;
  // @ViewChild('injectVertical', { static: true, read: ViewContainerRef }) injectVerticallyViewContainerRef: ViewContainerRef;
  @ViewChild('scrollContainer') scrollContainer: SimplebarAngularComponent;

  @Input() crosswordBoardConfig: CrosswordBoardConfig;
  @Input() availableHeight: number;

  simpleBarOptions = {};

  split: SplitFilter[];
  subSplit: SplitFilter[];

  words: CrosswordWordModel[];

  selectedWord: CrosswordWordModel;
  lastSelectedWord: CrosswordWordModel;
  definitionHeights: number[] = [];

  wordLists: WordList[] = [];

  private definitionExpandedElements: HTMLElement[] = [];
  private resizeObserver: any;
  // private subSplit: { filter: (word: CrosswordWordModel) => boolean; name: string }[];
  public dropListIds: string[];
  public selectedSubList: WordList;
  private selectedList: WordList;
  private softKeyboardOpen = false;

  public listMarginBottom = 150;
  public highlightRightAndWrongEnabled: boolean;
  public dragging = false;

  constructor(
    private definitionsListService: DefinitionsListService,
    private store: Store,
    public el: ElementRef,
    private helperFunctions: HelperFunctionsService,
    private cd: ChangeDetectorRef,
    private keyboardService: PuzzleKeyboardService
  ) {
    this.keyboardService.softKeyboardOpen$.subscribe((open) => {
      this.softKeyboardOpen = open;
    });
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
      .subscribe((enabled) => {
        this.highlightRightAndWrongEnabled = enabled;
      });

    this.definitionsListService.update.subscribe(() => {
      this.cd.detectChanges();
    });

    switch (this.crosswordBoardConfig.crosswordSidebar) {
      case 'definList':
      case 'definAccordion':
        this.initDefinitionSidebar();
        break;
    }

    this.definitionsListService.currentWord$.subscribe(async (word) => {
      if (!word) {
        this.selectedWord = word;
        return;
      }

      this.selectedWord = word;
      // this.scrollToActive(word);

      switch (this.crosswordBoardConfig.crosswordSidebar) {
        case 'definAccordion':
        case 'definList':
          if (this.crosswordBoardConfig.crosswordSidebar === 'definAccordion') {
            await this.helperFunctions.timeout(300);
            this.lastSelectedWord = word; // deselect after timeout
            this.cd.detectChanges();
          }
          await this.scrollToDefinition(word);
          break;
        case 'wordList':
          this.wordLists.map((wordList) => {
            wordList.wordLists.map((subList) => {
              if (subList.words.indexOf(word) !== -1) {
                this.selectedSubList = subList;
                this.selectedList = wordList;
                this.cd.detectChanges();
              }
            });
          });
          // await this.helperFunctions.timeout(300);
          await this.scrollToSubList();
          break;
      }
    });

    this.definitionsListService.listItems$.subscribe((items) => {
      this.words = items.sort((a, b) =>
        this.crosswordBoardConfig.fieldNumbers
          ? a.fieldNumber - b.fieldNumber
          : a.inputTiles[0].index - b.inputTiles[0].index
      );

      this.dropListIds = [];
      const tileIds = [];

      this.words.map((w) => {
        w.inputTiles.map((tile) => {
          if (tileIds.indexOf(tile.index) === -1) {
            tileIds.push(tile.index);
          }
        });
      });
      this.dropListIds = tileIds.map((id) => 'crossword-tile-' + id);
      this.dropListIds.push('swedish-container');

      if (this.crosswordBoardConfig.crosswordSidebar === 'wordList') {
        this.wordLists = [];
        switch (this.crosswordBoardConfig.splitWordList) {
          case 'no':
            this.wordLists.push({
              id: 'default',
              name: '',
              wordLists: [
                {
                  id: 'all',
                  name: '',
                  words: this.words,
                },
              ],
            });
            break;
          case 'orientation':
            this.wordLists.push({
              id: 'horizontal',
              name: 'CROSSWORD.orientations.horizontal',
              wordLists: [
                {
                  id: 'horizontal',
                  name: '',
                  words: this.words.filter((word) => word.direction === CrosswordDirectionsEnum.Right),
                },
              ],
            });
            this.wordLists.push({
              id: 'vertical',
              name: 'CROSSWORD.orientations.vertical',
              wordLists: [
                {
                  id: 'vertical',
                  name: '',
                  words: this.words.filter((word) => word.direction === CrosswordDirectionsEnum.Down),
                },
              ],
            });
            break;
          case 'length':
            let wordLists: WordList[] = [];
            this.words.map((word) => {
              if (wordLists[word.word.length] === undefined) {
                wordLists[word.word.length] = {
                  id: '' + word.word.length,
                  name: 'CROSSWORD.sidebar.letters.' + word.word.length + '_letters',
                  words: [],
                };
              }
              wordLists[word.word.length].words.push(word);
            });
            wordLists = wordLists.filter((e) => !!e);
            this.wordLists.push({
              id: 'default',
              name: '',
              wordLists,
            });
            break;
          case 'length&orientation':
            let subListHorizontal: WordList[] = [];
            this.words
              .filter((word) => word.direction === CrosswordDirectionsEnum.Right)
              .map((word) => {
                if (subListHorizontal[word.word.length] === undefined) {
                  subListHorizontal[word.word.length] = {
                    id: '' + word.word.length,
                    name: 'CROSSWORD.sidebar.letters.' + word.word.length + '_letters',
                    words: [],
                  };
                }
                subListHorizontal[word.word.length].words.push(word);
              });
            subListHorizontal = subListHorizontal.filter((entry) => !!entry);
            this.wordLists.push({
              id: 'horizontal',
              name: 'CROSSWORD.orientations.horizontal',
              wordLists: subListHorizontal,
            });
            let subListVerical: WordList[] = [];
            this.words
              .filter((word) => word.direction === CrosswordDirectionsEnum.Down)
              .map((word) => {
                if (subListVerical[word.word.length] === undefined) {
                  subListVerical[word.word.length] = {
                    id: '' + word.word.length,
                    name: 'CROSSWORD.sidebar.letters.' + word.word.length + '_letters',
                    words: [],
                  };
                }
                subListVerical[word.word.length].words.push(word);
              });
            subListVerical = subListVerical.filter((entry) => !!entry);
            this.wordLists.push({
              id: 'vertical',
              name: 'CROSSWORD.orientations.vertical',
              wordLists: subListVerical,
            });
            break;
        }
      }
    });

    this.definitionsListService.registerSidebar();
  }

  @HostListener('window:resize')
  onWindowsResize() {
    if (this.crosswordBoardConfig.crosswordSidebar === 'wordList') {
      this.scrollToSubList();
    } else {
      this.scrollToDefinition();
    }
  }

  async scrollToDefinition(word: CrosswordWordModel = this.selectedWord) {
    if (!word) {
      return;
    }

    const definitionWrapperElement = this.el.nativeElement.querySelector('#definition-wrapper-' + word.index);
    if (!definitionWrapperElement) {
      return;
    }

    const bodyWidth = document.body.offsetWidth;

    const scrollContainerElement = await this.scrollContainer.SimpleBar.getScrollElement();
    let scrollContainerElementHeight = bodyWidth < 600 ? scrollContainerElement.offsetHeight : this.availableHeight;

    if (this.softKeyboardOpen) {
      scrollContainerElementHeight -= 150;
    }
    const scrollContainerScrollTop = scrollContainerElement.scrollTop;

    const overlapAllowed = 10;

    let wordPosition = definitionWrapperElement.offsetTop;

    const wordHeight = definitionWrapperElement.offsetHeight;

    if (wordHeight > scrollContainerElementHeight - 96) {
      scrollContainerElement.scrollTop = wordPosition;
      return;
    }

    if (wordPosition + overlapAllowed < scrollContainerScrollTop) {
      const previousElement = definitionWrapperElement.previousSibling;
      if (
        previousElement &&
        previousElement.tagName === 'DIV' &&
        previousElement.classList.contains('definition-wrapper')
      ) {
        wordPosition -= 48;
      }
      scrollContainerElement.scrollTop = wordPosition;
    } else if (wordPosition - overlapAllowed + wordHeight > scrollContainerScrollTop + scrollContainerElementHeight) {
      const nextElement = definitionWrapperElement.nextSibling;
      if (nextElement && nextElement.tagName === 'DIV' && nextElement.classList.contains('definition-wrapper')) {
        wordPosition += 48;
      }
      scrollContainerElement.scrollTop = wordPosition + wordHeight - scrollContainerElementHeight;
    }
  }

  /**
   * Scrolls to correct element if worlist is ordered with length&orientation
   *
   * @private
   */

  private async scrollToSubList(
    selectedSubList: WordList = this.selectedSubList,
    selectedList: WordList = this.selectedList
  ) {
    if (!selectedSubList || !selectedList) {
      return;
    }

    const bodyWidth = document.body.offsetWidth;

    const listElement = this.el.nativeElement.querySelector('#wordlist-' + selectedList.id);
    const subListElement = this.el.nativeElement.querySelector(
      '#wordlist-' + selectedList.id + '-' + selectedSubList.id
    );

    if (!subListElement || !listElement) {
      return;
    }

    const scrollContainerElement = await this.scrollContainer.SimpleBar.getScrollElement();
    const scrollContainerElementHeight =
      bodyWidth < 600 ? scrollContainerElement.offsetHeight : this.availableHeight - (this.softKeyboardOpen ? 150 : 0);

    const scrollContainerScrollTop = scrollContainerElement.scrollTop;

    // let elementTop = subListElement.offsetTop;
    // if (!this.helperFunctions.isIOS()) {
    //   elementTop += listElement.offsetTop;
    // }

    const elementTop = subListElement.offsetTop + listElement.offsetTop;
    const elementHeight = subListElement.offsetHeight;
    const elementBottom = elementTop + elementHeight;

    if (elementHeight > scrollContainerElementHeight) {
      scrollContainerElement.scrollTop = elementTop;
    } else if (elementBottom > scrollContainerScrollTop + scrollContainerElementHeight) {
      // console.log('need to scroll down');
      scrollContainerElement.scrollTop = elementTop - scrollContainerElementHeight + elementHeight;
    } else if (elementTop < scrollContainerScrollTop) {
      // console.log('need to scroll up');
      scrollContainerElement.scrollTop = elementTop;
    }

    // console.log()
  }

  applyFilter(words: CrosswordWordModel[], filter: (word: CrosswordWordModel) => boolean): CrosswordWordModel[] {
    return words.filter(filter);
  }

  selectWord(word: CrosswordWordModel, event) {
    this.definitionsListService.setCurrentWord(word);
    event.preventDefault();
    event.stopPropagation();
  }

  extractDefinitionFields(): void {
    if (!this.words) {
      return;
    }

    this.words.map((word) => {
      const wordElement = this.el.nativeElement.querySelector('#word-expanded-' + word.index);
      if (!wordElement) {
        return;
      }
      this.definitionExpandedElements[word.index] = wordElement;
    });

    if (!this.definitionExpandedElements.length) {
      return;
    }

    // Register resizeObserver in order to keep definitionHeights upto date all the time
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.map((entry) => {
        const element = entry.target as HTMLElement;
        this.definitionHeights[this.definitionExpandedElements.indexOf(element)] = element.offsetHeight;
      });

      // this.listMarginBottom = this.definitionHeights.reduce((a, b) => a > b ? a : b) + 150;
    });
    this.definitionExpandedElements.map((word) => {
      this.resizeObserver.observe(word);
    });
  }

  ngAfterContentChecked(): void {
    if (this.crosswordBoardConfig.crosswordSidebar === 'definAccordion' && !this.definitionExpandedElements.length) {
      this.extractDefinitionFields();
    }
  }

  private initDefinitionSidebar() {
    switch (this.crosswordBoardConfig.splitDefinList) {
      case 'orientation':
        this.split = [
          {
            name: 'CROSSWORD.orientations.horizontal',
            filter: (word: CrosswordWordModel) => word.direction === CrosswordDirectionsEnum.Right,
          },
          {
            name: 'CROSSWORD.orientations.vertical',
            filter: (word: CrosswordWordModel) => word.direction === CrosswordDirectionsEnum.Down,
          },
        ];
        break;
      default:
        this.split = [
          {
            name: '',
            filter: () => true,
          },
        ];
        break;
    }
  }

  /**
   * remove dummies from list
   */
  onDragEnded(wordList: CrosswordWordModel[], word: CrosswordWordModel): void {
    const indexes = this.helperFunctions.getAllIndexes(wordList, word);
    for (let i = 1; i < indexes.length; i++) {
      wordList.splice(indexes[i], 1);
    }
  }

  /**
   * Adds dummy item to list
   */
  dropListExited(wordList: CrosswordWordModel[], word: CrosswordWordModel) {
    this.onDragEnded(wordList, word);
    const index = wordList.indexOf(word);
    wordList.splice(index, 0, word);
  }
}
